import { Component } from '@theme/component';

/**
 * Builds an autoplaying embed URL from a YouTube or Vimeo watch/share URL.
 *
 * @param {string} raw - The raw video URL.
 * @returns {string | null} The embed URL, or null if unrecognised.
 */
function buildEmbedUrl(raw) {
  if (!raw) return null;

  let url;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^www\.|^m\./, '');

  if (host === 'youtu.be') {
    const id = url.pathname.split('/').filter(Boolean)[0];
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : null;
  }
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    let id = url.searchParams.get('v');
    if (!id) {
      const match = url.pathname.match(/\/(?:embed|shorts|v)\/([^/?#]+)/);
      if (match) id = match[1];
    }
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : null;
  }
  if (host === 'vimeo.com') {
    const id = url.pathname.split('/').filter(Boolean).pop();
    return id && /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}?autoplay=1` : null;
  }
  if (host === 'player.vimeo.com') {
    return raw;
  }

  return null;
}

/**
 * `<video-modal-embed>` — lazily mounts a video iframe into its `frame` ref when
 * the surrounding `<dialog-component>` opens, and removes it on close so playback
 * stops and no iframe loads until the dialog is actually used.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} frame
 *
 * @extends {Component<Refs>}
 */
class VideoModalEmbed extends Component {
  requiredRefs = ['frame'];

  /** @type {Element | null} */
  #dialog = null;

  connectedCallback() {
    super.connectedCallback();
    this.#dialog = this.closest('dialog-component');
    if (!this.#dialog) return;
    this.#dialog.addEventListener('dialog:open', this.#mount);
    this.#dialog.addEventListener('dialog:close', this.#unmount);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.#dialog) return;
    this.#dialog.removeEventListener('dialog:open', this.#mount);
    this.#dialog.removeEventListener('dialog:close', this.#unmount);
  }

  #mount = () => {
    const embed = buildEmbedUrl(this.dataset.videoUrl ?? '');
    if (!embed) return;
    const iframe = document.createElement('iframe');
    iframe.src = embed;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.title = this.dataset.videoTitle ?? 'Video';
    this.refs.frame.replaceChildren(iframe);
  };

  #unmount = () => {
    this.refs.frame.replaceChildren();
  };
}

if (!customElements.get('video-modal-embed')) {
  customElements.define('video-modal-embed', VideoModalEmbed);
}
